// src/components/PromotionalLoadingComponent.js
import { useEffect, useState } from "react";
import { Car, ShieldCheck, Lock, CheckCircle2 } from "lucide-react";

/**
 * Standalone promotional loading screen shown while car rates are fetched.
 * Visual spec: white→mint card, green progress bar with a car marker that
 * rides the leading edge, dashed track ahead of progress, three trust badges.
 *
 * Usage:
 *   <PromotionalLoadingComponent onComplete={() => setCarDataLoading(false)} />
 */
const STEPS = [
  { limit: 20, text: "Connecting to global rental network..." },
  { limit: 45, text: "Checking vehicle availability..." },
  { limit: 70, text: "Comparing rates..." },
  { limit: 90, text: "Applying exclusive partner discounts..." },
  { limit: 100, text: "Finalizing your best available offers..." },
];

export const LoadingComponent = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [message, setMessage] = useState(STEPS[0].text);

  useEffect(() => {
    let value = 0;
    const interval = setInterval(() => {
      value += value < 30 ? 0.4 : value < 70 ? 0.8 : value < 90 ? 0.25 : 0.1;
      value = Math.min(value, 100);
      setProgress(value);

      const step = STEPS.find((s) => value <= s.limit);
      if (step) setMessage(step.text);

      if (value >= 100) {
        clearInterval(interval);
        setTimeout(() => onComplete?.(), 800);
      }
    }, 30);
    return () => clearInterval(interval);
  }, [onComplete]);

  return (
    <div className="w-full flex items-center justify-center py-10 md:py-16 px-4">
      <div
        className="relative w-full max-w-xl rounded-3xl shadow-2xl px-6 md:px-12 py-10 md:py-12 text-center overflow-hidden"
        style={{
          background: "linear-gradient(160deg, #ffffff 0%, #eafaf1 100%)",
        }}
      >
        {/* Heading */}
        <h2 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight">
          Searching the best cars for you
        </h2>
        <p className="mt-2 text-[11px] md:text-xs font-semibold text-slate-400 uppercase tracking-widest">
          {message}
        </p>

        {/* Progress bar with riding car marker */}
        <div className="relative mt-12 md:mt-14 mb-3">
          <div className="relative h-[3px] w-full rounded-full overflow-hidden">
            {/* dashed track (full width, behind) */}
            <div
              className="absolute inset-0 rounded-full"
              style={{
                backgroundImage:
                  "repeating-linear-gradient(to right, #cbd5d9 0px, #cbd5d9 4px, transparent 4px, transparent 9px)",
              }}
            />
            {/* solid green fill */}
            <div
              className="absolute inset-y-0 left-0 rounded-full transition-all duration-300 ease-out"
              style={{
                width: `${progress}%`,
                background:
                  "linear-gradient(to right, #22c55e, #16a34a)",
              }}
            />
          </div>

          {/* Car icon riding the leading edge */}
          <div
            className="absolute -top-[15px] transition-all duration-300 ease-out"
            style={{ left: `${progress}%`, transform: "translateX(-50%)" }}
          >
            <Car
              className="w-7 h-7 md:w-8 md:h-8 text-green-500"
              strokeWidth={2}
              fill="white"
            />
          </div>

          {/* Percentage label */}
          <div className="mt-7 text-sm font-bold text-slate-400 tracking-wide">
            {Math.round(progress)}% COMPLETE
          </div>
        </div>

        {/* Divider */}
        <div className="mt-10 mb-6 border-t border-slate-200/70" />

        {/* Trust badges */}
        <div className="grid grid-cols-3 gap-2">
          <Badge icon={ShieldCheck} label="Secure Data" />
          <Badge icon={Lock} label="Encrypted" />
          <Badge icon={CheckCircle2} label="Verified" />
        </div>
      </div>
    </div>
  );
};

const Badge = ({ icon: Icon, label }) => (
  <div className="flex flex-col items-center gap-2">
    <div className="w-9 h-9 md:w-10 md:h-10 rounded-full border-2 border-green-400 flex items-center justify-center">
      <Icon className="w-4 h-4 md:w-5 md:h-5 text-green-500" strokeWidth={2.2} />
    </div>
    <span className="text-[10px] md:text-[11px] font-bold text-slate-700 uppercase tracking-tight">
      {label}
    </span>
  </div>
);

export default LoadingComponent;
